import React from 'react';
import SalesOverviewService from '../services/SalesOverviewService';
import { CHART_COLORS, CHART_SEMANTIC } from '../../../utils/chartColors';

export interface OfferFunnelProps {
  counts: {
    DRAFT: number;
    SENT: number;
    ACCEPTED: number;
    REJECTED: number;
    EXPIRED: number;
  };
  amounts?: {
    DRAFT: number;
    SENT: number;
    ACCEPTED: number;
    REJECTED: number;
    EXPIRED: number;
  };
  conversionRate?: number;
}

interface FunnelItem {
  key: string;
  label: string;
  count: number;
  amount: number;
  color: string;
}

export default function OfferFunnelChart({ counts, amounts, conversionRate }: OfferFunnelProps): React.JSX.Element {
  const funnelItems: FunnelItem[] = [
    { key: 'ACCEPTED', label: 'Accepted', count: counts.ACCEPTED, amount: amounts?.ACCEPTED ?? 0, color: CHART_SEMANTIC.accepted },
    { key: 'SENT', label: 'Sent', count: counts.SENT, amount: amounts?.SENT ?? 0, color: CHART_SEMANTIC.sent },
    { key: 'DRAFT', label: 'Draft', count: counts.DRAFT, amount: amounts?.DRAFT ?? 0, color: CHART_SEMANTIC.draft },
    { key: 'REJECTED', label: 'Rejected', count: counts.REJECTED, amount: amounts?.REJECTED ?? 0, color: CHART_SEMANTIC.rejected },
    { key: 'EXPIRED', label: 'Expired', count: counts.EXPIRED, amount: amounts?.EXPIRED ?? 0, color: CHART_SEMANTIC.expired },
  ];

  const maxCount = Math.max(...funnelItems.map((item) => item.count), 1);

  const displayConversionRate = conversionRate ?? 0;
  const isGoodConversion = displayConversionRate >= 30;

  return (
    <div
      style={{
        background: 'white',
        borderRadius: '12px',
        padding: '20px 24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        height: '100%',
      }}
    >
      <div style={{ fontSize: '16px', fontWeight: 600, color: '#021a4a', marginBottom: '16px' }}>
        Offer Funnel
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {funnelItems.map((item) => (
          <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '70px', fontSize: '13px', color: '#666', fontWeight: 500 }}>
              {item.label}
            </div>
            <div
              style={{
                flex: 1,
                height: '24px',
                background: '#f1f5f9',
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${(item.count / maxCount) * 100}%`,
                  height: '100%',
                  background: item.color,
                  borderRadius: '4px',
                  minWidth: item.count > 0 ? '4px' : '0',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
            <div
              style={{
                width: '30px',
                fontSize: '13px',
                fontWeight: 600,
                color: '#021a4a',
                textAlign: 'right',
              }}
            >
              {item.count}
            </div>
            {amounts && (
              <div style={{ width: '90px', fontSize: '12px', color: '#666', textAlign: 'right' }}>
                {SalesOverviewService.formatCurrency(item.amount)}
              </div>
            )}
          </div>
        ))}
      </div>

      <div
        style={{
          marginTop: '16px',
          paddingTop: '16px',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span style={{ fontSize: '13px', color: '#666' }}>Conversion Sent → Accepted</span>
        <span
          style={{
            fontSize: '16px',
            fontWeight: 700,
            color: isGoodConversion ? CHART_COLORS.success : CHART_COLORS.yellow,
          }}
        >
          {displayConversionRate.toFixed(0)}%
        </span>
      </div>
    </div>
  );
}
